import { WebSocketContext } from '@/contexts/WebSocketContext';
import { POPUP_TYPE } from '@/models/Popup';
import { CMDPsChangePassword } from '@/models/cmd/live';
import { getMainUserState, getResultState } from '@/modules/main/selector';
import { resultSliceActions } from '@/modules/main/slice/result';
import { popupSliceActions } from '@/modules/popup/slice';
import { RootState } from '@/store/store';
import { HelpOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChangeEvent,
    MouseEvent,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DialogContext, SettingDialogProps } from '..';
import '../index.scss';
import { PasswordScoreBar } from './changePassword/PasswordScoreBar';
import { TickButton } from '@/modules/popup/svg';
export const ChangePassword = (props: SettingDialogProps) => {
    const { setIsOpen } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { sendCommand } = useContext(WebSocketContext);
    const { setExtraCloseHandler } = useContext(DialogContext);
    const [isHintOpen, setHintOpen] = useState(false);
    const [passwords, setPasswords] = useState({
        oldPassword: '',
        showOldPassword: false,
        newPassword: '',
        showNewPassword: false,
        confirmPassword: '',
        showConfirmPassword: false,
    });
    const { isPasswordChange } = useSelector((state: RootState) =>
        getResultState(state)
    );
    const { LobbyType } = useSelector((state: RootState) =>
        getMainUserState(state)
    );
    const [errorMsg, setErrorMsg] = useState('');
    useEffect(() => {
        if (isPasswordChange) {
            setErrorMsg('');
            dispatch(resultSliceActions.reset());
            setIsOpen(false);
        }
    }, [isPasswordChange]);

    const submit = async () => {
        if (passwords.oldPassword == '' || passwords.newPassword == '') {
            setErrorMsg('password.password_empty');
            return;
        }
        if (passwords.newPassword != passwords.confirmPassword) {
            setErrorMsg('password.password_confirm_fail');
            return;
        }
        if (passwords.oldPassword == passwords.newPassword) {
            setErrorMsg('password.password_same');
            return;
        }

        const Exp = '^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])([a-zA-Z0-9]+){8,15}$';
        if (!passwords.newPassword.match(Exp)) {
            setErrorMsg('password.password_rule');
            return;
        }

        dispatch(
            popupSliceActions.open(
                'password.updating_password',
                POPUP_TYPE.POPUP
            )
        );

        const cmd = new CMDPsChangePassword();
        cmd.OldPassword = passwords.oldPassword;
        cmd.NewPassword = passwords.newPassword;
        cmd.type = LobbyType;
        sendCommand(cmd);
    };

    const handleClickShowPassword =
        (key: keyof typeof passwords) => (event: MouseEvent) => {
            console.log(event);
            setPasswords({
                ...passwords,
                [key]: !passwords[key],
            });
        };
    const handleMouseDownPassword = (event: MouseEvent) => {
        event.preventDefault();
    };
    const handleOpen = () => {
        setHintOpen(!isHintOpen);
    };

    const handlePasswordChange =
        (key: keyof typeof passwords, maxLength: number) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            const Exp = '^[A-Za-z0-9_-]*$';
            if (event.target.value.match(Exp)) {
                setPasswords({
                    ...passwords,
                    [key]: event.target.value.slice(0, maxLength),
                });
            }
        };

    useEffect(() => {
        setExtraCloseHandler(() => () => {});
    }, []);

    return (
        <Stack
            sx={{
                alignSelf: 'center',
                width: '92vi',
                height: '85%',
            }}
        >
            <Typography color={'#BF9A5D'} fontSize={'1rem'} lineHeight={'3rem'}>
                {t('password.current_password')}
            </Typography>
            <Grid alignItems={'center'} justifyContent={'center'}>
                <OutlinedInput
                    sx={{
                        width: '100%',
                        backgroundColor: '#1c1c1c',
                        borderRadius: '1vw',
                        fontSize: '21px',
                    }}
                    type={passwords.showOldPassword ? 'text' : 'password'}
                    // label={t('feedback.detail')}
                    value={passwords.oldPassword}
                    onChange={handlePasswordChange('oldPassword', 15)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClickShowPassword(
                                    'showOldPassword'
                                )}
                                onMouseDown={handleMouseDownPassword}
                            >
                                {passwords.showOldPassword ? (
                                    <VisibilityOff sx={{ color: '#BF9A5D' }} />
                                ) : (
                                    <Visibility sx={{ color: '#BF9A5D' }} />
                                )}
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </Grid>
            <Stack direction={'row'} justifyContent="space-between">
                <Typography
                    color={'#BF9A5D'}
                    fontSize={'1rem'}
                    lineHeight={'3rem'}
                >
                    {t('password.new_password')}
                </Typography>

                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                        open={isHintOpen}
                        title={
                            <span style={{ whiteSpace: 'pre-line' }}>
                                <Typography
                                    fontSize={'0.9rem'}
                                    lineHeight={'0.5rem'}
                                >
                                    {'\n' + t('password.password_strength')}
                                </Typography>
                                {'\n' +
                                    t('password.good_password_title') +
                                    '\n' +
                                    t('password.good_password_content')}
                            </span>
                        }
                    >
                        <IconButton onClick={handleOpen}>
                            <HelpOutline sx={{ color: '#58585B' }} />
                        </IconButton>
                    </Tooltip>
                </Typography>
            </Stack>
            <Grid
                alignItems={'center'}
                justifyContent={'center'}
                width={'100%'}
            >
                <FormControl
                    sx={{
                        width: '92vi',
                    }}
                >
                    <OutlinedInput
                        sx={{
                            width: '100%',
                            backgroundColor: '#1c1c1c',
                            borderRadius: '1vw',
                            fontSize: '21px',
                        }}
                        type={passwords.showNewPassword ? 'text' : 'password'}
                        // label={t('feedback.detail')}
                        value={passwords.newPassword}
                        onChange={handlePasswordChange('newPassword', 15)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleClickShowPassword(
                                        'showNewPassword'
                                    )}
                                    onMouseDown={handleMouseDownPassword}
                                >
                                    {passwords.showNewPassword ? (
                                        <VisibilityOff
                                            sx={{ color: '#BF9A5D' }}
                                        />
                                    ) : (
                                        <Visibility sx={{ color: '#BF9A5D' }} />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    <PasswordScoreBar password={passwords.newPassword} />
                </FormControl>
            </Grid>
            <Typography color={'#BF9A5D'} fontSize={'1rem'} lineHeight={'2rem'}>
                <pre>{'\n' + t('password.confirm_password')}</pre>
            </Typography>
            <Grid alignItems={'center'} justifyContent={'center'}>
                <OutlinedInput
                    sx={{
                        width: '100%',
                        backgroundColor: '#1c1c1c',
                        borderRadius: '1vw',
                        fontSize: '21px',
                    }}
                    type={passwords.showConfirmPassword ? 'text' : 'password'}
                    // label={t('feedback.detail')}
                    value={passwords.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword', 15)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClickShowPassword(
                                    'showConfirmPassword'
                                )}
                                onMouseDown={handleMouseDownPassword}
                            >
                                {passwords.showConfirmPassword ? (
                                    <VisibilityOff sx={{ color: '#BF9A5D' }} />
                                ) : (
                                    <Visibility sx={{ color: '#BF9A5D' }} />
                                )}
                            </IconButton>
                        </InputAdornment>
                    }
                />
                {errorMsg !== '' && (
                    <Typography
                        color={'#DC3E2F'}
                        fontSize={'1rem'}
                        lineHeight={'3rem'}
                    >
                        {t(errorMsg)}
                    </Typography>
                )}
                <Typography color={'#58585B'} fontSize={'1rem'}>
                    {t('password.password_rule')}
                </Typography>
            </Grid>
            <Box
                width={'100%'}
                height={'100%'}
                justifyContent={'center'}
                alignItems={'flex-end'}
                display={'flex'}
                sx={{ cursor: 'pointer' }}
            >
                <TickButton height={'3rem'} onClick={submit} />
            </Box>
        </Stack>
    );
};
